import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { api_routes, db_tables, features, routes } from '@app/consts';
import { FeatureService } from '@app/core/services/feature.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { RowActionButtonInput } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employment-records/components/employment-record-details/components/data-grid-comp/models/data-grid-comp.model';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SecurityProtectedBase } from '@app/shared/components/security-protected/security-protected';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { defer, forkJoin } from 'rxjs';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { ArticlesService } from './services/articles.service';
import { Article } from './models/article.model';

@Component({
  selector: 'app-articles',
  templateUrl: './articles.component.html',
  styleUrls: ['./articles.component.scss']
})
export class ArticlesComponent extends SecurityProtectedBase implements OnInit {
  public columns: any[] = [
    { field: "title", title: "Title", tableId: "tbl_ArticlesTitle", },
    { field: "splashText", title: "Splash Text", type: "longText", tableId: "tbl_ArticlesSplashText" },
    { field: "publishOn", title: "Publish On", type: "date", tableId: "tbl_ArticlesPublishOn" },
    { field: "expiresOn", title: "Expires On", type: "date", tableId: "tbl_ArticlesExpiresOn" },
    { field: "showImageOnSplashPage", title: "Show Image On Splash Page", type: "booleanToggle", tableId: "tbl_ArticlesShowImageOnSplashPage" },
    { field: "views", title: "Views", tableId: "tbl_ArticlesViews", },
    { field: 'regions', title: "Regions", type: 'array', arrayItemField: 'name', tableId: "tbl_ArticlesRegions" },
  ];

  filterCategories: any[] = [
    { field: "title", title: "Title", tableId: "tbl_ArticlesTitle", type: "String", dataType: 'String' },
    { field: "publishOn", title: "Publish On", tableId: "tbl_ArticlesPublishOn", type: "Date", dataType: 'Date' },
    { field: "expiresOn", title: "Expires On", tableId: "tbl_ArticlesExpiresOn", type: "Date", dataType: 'Date' },
    { field: "views", title: "Views", tableId: "tbl_ArticlesViews", type: "Number", dataType: 'Number' },
    { field: "showImageOnSplashPage", title: "Show Image On Splash Page", tableId: "tbl_ArticlesShowImageOnSplashPage", type: "Boolean", dataType: 'Boolean' },
    { field: "regions.name", title: "Regions", tableId: "tbl_ArticlesRegions", type: "String", dataType: 'String' },
  ];

  sortableColumns: any[] = [
    { field: "title", sortValue: "title" },
    { field: "splashText", sortValue: "splashText" },
    { field: "publishOn", sortValue: "publishOn" },
    { field: "expiresOn", sortValue: "expiresOn" },
    { field: "views", sortValue: "views" },
    { field: "showImageOnSplashPage", sortValue: "showImageOnSplashPage" },
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 20;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: any;
  formValid: any;
  sortString: string = 'version.createdOn-desc';
  isLoadingPermissions: boolean = true;
  filterString: string;
  gridId: string = 'articles';
  tableId: string = db_tables.Articles;
  getRequest: any;
  searchValue: string;
  searchFilterString: string;

  constructor(
    private overlayService: OverlayService,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private router: Router,
    private dialog: MatDialog,
    private articlesService: ArticlesService,
    featureService: FeatureService
  ) {
    super(featureService, features.ARTICLES);
  }

  ngOnInit(): void {
    if (this.hasFeatureAccess) {
      this.getArticles();
    }
  }

  getArticles() {
    this.isLoading = true;

    // Combine filterString and searchFilterString if both exist
    let combinedFilter = '';
    if (this.filterString && this.searchFilterString) {
      combinedFilter = `(${this.filterString}) AND (${this.searchFilterString})`;
    } else if (this.filterString) {
      combinedFilter = this.filterString;
    } else if (this.searchFilterString) {
      combinedFilter = this.searchFilterString;
    } else {
      combinedFilter = '';
    }
    // Use combinedFilter in the service call

    this.getRequest = this.articlesService.getArticles(this.pageSize, this.skip, combinedFilter, this.sortString)
      .subscribe(
        {
          next: (v) => {
            this.gridDataResult = {
              data: v.data,
              total: v.total
            };
            this.isLoading = false;
          },
          error: (e) => this.isLoading = false,
          complete: () => this.isLoading = false
        }
      );
  }

  editOrCreateArticle(article?: Article) {
    if (article?.id) {
      this.router.navigate([`${routes.SITE_SETTINGS_NO_SLASH}${routes.ARTICLES}${routes.EDITOR}/${article.id}`]);
    }
    else {
      this.router.navigate([`${routes.SITE_SETTINGS_NO_SLASH}${routes.ARTICLES}${routes.EDITOR}`]);
    }

  }

  openConfirmDeleteDialog(importIds: string[]) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('AreYouSureYouWantToDeleteThis')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.deleteArticles(importIds);
        }
      }
    );
  }

  deleteArticles(articleIds: string[]) {
    const observables = articleIds.map(selectedItem => defer(() => this.articlesService.deleteArticle(selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
      .subscribe(
        {
          next: (v) => {
            this.clearSelectedItems = !this.clearSelectedItems;
            this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
            this.getArticles();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `(Title like "${variant}")`;
        }
      });

      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getArticles();
  }
}
